import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { MdShowChart } from "react-icons/md";

const VmMonthlyCost = ({
  directCost,
  reservationCost,
  sharedCost,
  loading,
  vmName,
}) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const getChartData = () => {
    if (
      directCost === undefined &&
      reservationCost === undefined &&
      sharedCost === undefined
    ) {
      return [];
    }
    return [
      { name: "Direct Cost", cost: directCost || 0 },
      { name: "Reservation", cost: reservationCost || 0 },
      { name: "Shared Cost", cost: sharedCost || 0 },
    ];
  };

  const chartData = getChartData();

  const totalCost =
    (directCost || 0) + (reservationCost || 0) + (sharedCost || 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-gray-300 text-sm mb-1">{label}</p>
          <p className="text-primary-cyan text-lg font-bold">
            {formatAmount(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-primary-dark rounded-xl p-6 border border-gray-700/50">
        <div className="text-center py-8">
          <div className="inline-block w-8 h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-3">Loading cost details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-dark rounded-xl p-6 border border-gray-700/50">
      <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
        <MdShowChart className="text-primary-cyan text-2xl" />
        <h3 className="text-xl font-semibold text-white">Cost Breakdown</h3>
      </div>

      {chartData.length === 0 ||
      (directCost === 0 && reservationCost === 0 && sharedCost === 0) ? (
        <div className="text-center py-8">
          <p className="text-gray-400">
            No cost data available for this period
          </p>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#374151"
                vertical={false}
              />
              <XAxis
                dataKey="name"
                stroke="#9CA3AF"
                tick={{ fill: "#9CA3AF", fontSize: 12 }}
                angle={-15}
                textAnchor="end"
                height={60}
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: "#9CA3AF", fontSize: 12 }}
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              <Bar
                dataKey="cost"
                name="Cost"
                fill="#dc2626"
                radius={[8, 8, 0, 0]}
                animationDuration={1000}
                activeBar={false}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      index === 0
                        ? "#dc2626"
                        : index === 1
                          ? "#3b82f6"
                          : "#10b981"
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-6 pt-4 border-t border-gray-700">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary-cyan"></div>
                <span className="text-gray-400 font-medium">Total Cost</span>
              </div>
              <span className="text-primary-cyan text-2xl font-bold">
                {formatAmount(totalCost)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VmMonthlyCost;